import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PaperPlaneTilt, CheckCircle, ArrowCounterClockwise, Envelope, Buildings, FileText, Truck } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYP_LABEL = {
  anspruchsschreiben: "Anspruchsschreiben",
  unfallhergang: "Unfallhergang",
  klageschrift: "Klageschrift",
  owi_einspruch: "Einspruch (OWi)",
  bezifferungsschreiben: "Bezifferung",
};

const VERSANDARTEN = [
  { value: "post", label: "Post (Brief)", icon: Truck },
  { value: "email", label: "E-Mail", icon: Envelope },
  { value: "bea", label: "beA (Anwaltspostfach)", icon: Buildings },
  { value: "fax", label: "Fax", icon: FileText },
  { value: "persoenlich", label: "Persönlich übergeben", icon: CheckCircle },
];

export default function Versand() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("offen");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeSatz, setActiveSatz] = useState(null);
  const [form, setForm] = useState({ versendet_per: "post", empfaenger: "", notiz: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/versand?status=${tab}`);
      setRows(r.data.schriftsaetze || []);
    } catch {
      toast.error("Konnte Versand-Liste nicht laden");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const openVersand = (s) => {
    setActiveSatz(s);
    setForm({ versendet_per: "post", empfaenger: "", notiz: "" });
    setDialogOpen(true);
  };

  const submitVersand = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/schriftsaetze/${activeSatz.id}/versand`, form);
      toast.success("Als versendet markiert");
      setDialogOpen(false);
      load();
    } catch {
      toast.error("Fehler beim Markieren");
    }
  };

  const undoVersand = async (s) => {
    if (!window.confirm("Versand-Status zurücksetzen?")) return;
    try {
      await api.post(`/schriftsaetze/${s.id}/versand-zuruecksetzen`);
      toast.success("Zurückgesetzt");
      load();
    } catch (err) {
      toast.error(errMsg(err, "Zurücksetzen fehlgeschlagen"));
    }
  };

  return (
    <div className="space-y-8" data-testid="versand-page">
      <header>
        <p className="overline text-[#D32F2F] mb-2">Workflow · 6</p>
        <h1 className="font-serif text-3xl lg:text-4xl">Versand</h1>
        <p className="text-sm text-[#6B7280] mt-2 max-w-2xl">
          Schriftsätze, die zum Versand bereit sind oder bereits versendet wurden. Jeder Versand wird mit Zeitstempel und Methode protokolliert.
        </p>
      </header>

      <div className="flex gap-1 border-b border-[#E5E7EB]">
        <button
          onClick={() => setTab("offen")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "offen" ? "border-[#1A2238] text-[#1A2238]" : "border-transparent text-[#6B7280] hover:text-[#0A192F]"}`}
          data-testid="versand-tab-offen"
        >
          Bereit zum Versand
        </button>
        <button
          onClick={() => setTab("versendet")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "versendet" ? "border-[#1A2238] text-[#1A2238]" : "border-transparent text-[#6B7280] hover:text-[#0A192F]"}`}
          data-testid="versand-tab-versendet"
        >
          Versendet
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-[#9CA3AF]">Lade …</p>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-12 text-center">
          <PaperPlaneTilt size={32} weight="duotone" className="text-[#1A2238] mx-auto mb-3" />
          <p className="font-serif text-xl mb-1">{tab === "offen" ? "Keine offenen Versendungen" : "Noch nichts versendet"}</p>
          <p className="text-sm text-[#6B7280]">{tab === "offen" ? "Generieren Sie Schriftsätze in der Akte-Detailseite." : "Die ersten Versendungen erscheinen hier."}</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map(s => (
            <li key={s.id} className="bg-white border border-[#E5E7EB] rounded-lg p-4 flex flex-wrap items-center gap-4" data-testid={`versand-row-${s.id}`}>
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{TYP_LABEL[s.typ] || s.typ}</span>
                  {s.aktenzeichen && (
                    <Link to={`/app/akten/${s.akte_id}`} className="text-xs text-[#1A2238] underline" data-testid={`versand-akte-link-${s.id}`}>
                      {s.aktenzeichen}
                    </Link>
                  )}
                </div>
                <p className="text-sm text-[#4B5563]">{s.mandant_name || "—"}</p>
                <p className="text-xs text-[#9CA3AF] mt-0.5">
                  Erstellt: {new Date(s.created_at).toLocaleString("de-DE")}
                  {s.versendet_am && (
                    <> · Versendet {new Date(s.versendet_am).toLocaleString("de-DE")} per {s.versendet_per}</>
                  )}
                  {s.empfaenger && <> · an {s.empfaenger}</>}
                </p>
              </div>
              {s.versendet_am ? (
                <Button onClick={() => undoVersand(s)} variant="outline" size="sm" data-testid={`versand-undo-${s.id}`}>
                  <ArrowCounterClockwise size={14} className="mr-1" /> Zurücksetzen
                </Button>
              ) : (
                <Button onClick={() => openVersand(s)} className="bg-[#1A2238] hover:bg-[#2C3859]" size="sm" data-testid={`versand-mark-${s.id}`}>
                  <PaperPlaneTilt size={14} className="mr-1" /> Versendet markieren
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Versand protokollieren</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitVersand} className="space-y-4">
            <div>
              <Label>Versandart</Label>
              <Select value={form.versendet_per} onValueChange={(v) => setForm({ ...form, versendet_per: v })}>
                <SelectTrigger data-testid="versand-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VERSANDARTEN.map(v => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Empfänger (optional)</Label>
              <Input value={form.empfaenger} onChange={(e) => setForm({ ...form, empfaenger: e.target.value })} placeholder="z.B. HUK-Coburg, Schadenabt." data-testid="versand-empfaenger" />
            </div>
            <div>
              <Label>Notiz (optional)</Label>
              <Textarea rows={2} value={form.notiz} onChange={(e) => setForm({ ...form, notiz: e.target.value })} placeholder="z.B. Einschreiben mit Rückschein" data-testid="versand-notiz" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
              <Button type="submit" className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="versand-submit">Versand bestätigen</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
